import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormHelperText,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import AddIcon from "@mui/icons-material/Add";
import { Controller } from "react-hook-form";
import {
  VisuallyHiddenInput,
  UploadBox,
  SectionTitle,
} from "./StyledComponents";
import CustomDatePicker from "./CustomDatePicker";
import DocIdField from "./DocIdField";
import CustomAutocomplete from "./CustomAutocomplete";
import PdfFileNameGenerator from "./PdfFileNameGenerator";
import AddNewDropdownValue from "./AddNewDropdownValue";
import UnenteredDocumentsSelector from "./UnenteredDocumentsSelector";

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error in form section:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 2, border: "1px solid red", borderRadius: 1 }}>
          <Typography color="error">
            Error loading this section. Please try refreshing the page.
          </Typography>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Form Section Component
const FormSection = ({ children, title }) => {
  return (
    <ErrorBoundary>
      <Box sx={{ mb: 4 }}>
        <SectionTitle variant="h6">{title}</SectionTitle>
        {children}
      </Box>
    </ErrorBoundary>
  );
};

// Validation Utilities
const validateFormState = (formValues) => {
  const errors = [];

  if (!formValues.pdfFile) {
    errors.push("PDF file is required");
  }
  if (!formValues.generatedFileName) {
    errors.push("Generated filename is required");
  }
  if (!formValues.title1) {
    errors.push("Document title is required");
  }
  if (!formValues.sitename1) {
    errors.push("Primary site name is required");
  }

  return errors;
};

const PdfUpload = React.memo(
  ({
    handleDragOver,
    handleDrop,
    handleFileChange,
    pdfName,
    originalPdfName,
    errors,
  }) => (
    <Grid size={12}>
      <UploadBox
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => document.getElementById("pdf-upload").click()}
        hasFile={!!pdfName}
      >
        <FilePresentIcon
          sx={{
            fontSize: 32,
            color: pdfName ? "#0A1929" : "primary.main",
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            color="textSecondary"
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {pdfName
              ? originalPdfName
              : "Drag and drop your PDF file here, or click to browse"}
          </Typography>
        </Box>
        <VisuallyHiddenInput
          id="pdf-upload"
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
        />
      </UploadBox>
      {errors.pdfFile && (
        <FormHelperText error sx={{ mt: 1, textAlign: "center" }}>
          {errors.pdfFile.message}
        </FormHelperText>
      )}
    </Grid>
  )
);

const FormContent = React.memo(
  ({
    handleSubmit,
    onSubmit,
    handleDragOver,
    handleDrop,
    handleFileChange,
    handleKeyDown,
    pdfName,
    errors,
    control,
    setValue,
    formOptions,
    isSubmitting,
    fetchHighestDocId,
    isLoadingDocId,
    isLoadingOptions,
    instance,
    accounts,
    tenantId,
    onAddAuthor,
    onAddSitename1,
    onAddSitename2,
    onAddSitename3,
    onAddAddressname,
    onUnenteredDocumentSelect,
  }) => {
    const [additionalSites, setAdditionalSites] = useState(0);
    const [isAddDropdownValueDialogOpen, setIsAddDropdownValueDialogOpen] =
      useState(false);
    const [currentDropdownField, setCurrentDropdownField] = useState(null);
    const [isResourceLoading, setIsResourceLoading] = useState(true);
    const [isUnenteredDocumentsOpen, setIsUnenteredDocumentsOpen] = useState(false);
    const [reloadAbbreviations, setReloadAbbreviations] = useState(0);

    // Add effect to manage resource loading state
    useEffect(() => {
      const checkResourcesLoaded = () => {
        // Check if all required resources are loaded
        const resourcesLoaded = !isLoadingOptions && !isLoadingDocId;
        setIsResourceLoading(!resourcesLoaded);
      };

      checkResourcesLoaded();
    }, [isLoadingOptions, isLoadingDocId]);

    useEffect(() => {
      if (!isSubmitting) {
        setAdditionalSites(0);
        const fileInput = document.getElementById("pdf-upload");
        if (fileInput && !control._formValues.pdfFile) {
          fileInput.value = "";
        }
      }
    }, [isSubmitting, control]);

    const handleAddSite = () => {
      if (additionalSites < 2) {
        setAdditionalSites((prev) => prev + 1);
      }
    };

    const handleCloseDropdownValueDialog = (fieldName) => {
      setIsAddDropdownValueDialogOpen(false);
      if (fieldName) {
        setValue(fieldName, "", { shouldValidate: true });
      }
      setCurrentDropdownField(null);
    };

    const handleAddNewDropdownValue = (fieldName) => {
      setCurrentDropdownField(fieldName);
      setIsAddDropdownValueDialogOpen(true);
    };

    const handleDropdownValueSubmit = async (data) => {
      try {
        if (!data || !data.name) {
          throw new Error("Invalid dropdown value data");
        }

        // First update the form options
        switch (currentDropdownField) {
          case "title":
            if (onAddAuthor) await onAddAuthor(data);
            // Trigger reload of abbreviations when author is added
            setReloadAbbreviations(prev => prev + 1);
            break;
          case "sitename1":
            if (onAddSitename1) await onAddSitename1(data);
            break;
          case "sitename2":
            if (onAddSitename2) await onAddSitename2(data);
            break;
          case "sitename3":
            if (onAddSitename3) await onAddSitename3(data);
            break;
          case "addressname":
            if (onAddAddressname) await onAddAddressname(data);
            break;
          default:
            console.warn(
              `Unhandled dropdown field type: ${currentDropdownField}`
            );
        }

        // Then update the form value for non-portarea fields
        setValue(currentDropdownField, data.name, {
          shouldValidate: true,
          shouldDirty: true,
        });
      } catch (error) {
        console.error("Error updating dropdown value:", error);
        // You might want to show an error toast here
      } finally {
        handleCloseDropdownValueDialog();
      }
    };

    const handleFormSubmit = async (data) => {
      try {
        const validationErrors = validateFormState(data);
        if (validationErrors.length > 0) {
          throw new Error(validationErrors.join("\n"));
        }
        await onSubmit(data);
      } catch (error) {
        console.error("Error in form submission:", error);
        // Error handling is done in the parent component
      }
    };

    const handleUnenteredDocumentSelect = (document) => {
      onUnenteredDocumentSelect(document);
    };

    return (
      <form onSubmit={handleSubmit(handleFormSubmit)} onKeyDown={handleKeyDown}>
        <FormSection title="Document Information">
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <PdfUpload
                handleDragOver={handleDragOver}
                handleDrop={handleDrop}
                handleFileChange={handleFileChange}
                pdfName={pdfName}
                originalPdfName={control._formValues.pdfName}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setIsUnenteredDocumentsOpen(true)}
                  startIcon={<CloudUploadIcon />}
                >
                  Select from Unentered Documents
                </Button>
                <UnenteredDocumentsSelector
                  open={isUnenteredDocumentsOpen}
                  onClose={() => setIsUnenteredDocumentsOpen(false)}
                  onSelect={handleUnenteredDocumentSelect}
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <DocIdField
                control={control}
                errors={errors}
                fetchHighestDocId={fetchHighestDocId}
                isLoadingDocId={isLoadingDocId}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 9 }}>
              <Controller
                name="title1"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Title"
                    error={!!errors.title1}
                    helperText={errors.title1?.message}
                    placeholder="Enter document title"
                  />
                )}
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{ display: "flex", justifyContent: "flex-start" }}
            >
              <Controller
                name="reportDate"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    value={value}
                    onChange={onChange}
                    error={!!errors.reportDate}
                    helperText={errors.reportDate?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <CustomAutocomplete
                name="title"
                control={control}
                label="Author"
                options={["Add New", ...formOptions.title]}
                error={!!errors.title}
                helperText={errors.title?.message}
                isLoading={isLoadingOptions}
                onChange={(_, newValue) => {
                  if (newValue === "Add New") {
                    handleAddNewDropdownValue("title");
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <Controller
                name="Report_x0020_Submittee"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Report Submittee"
                    error={!!errors.Report_x0020_Submittee}
                    helperText={errors.Report_x0020_Submittee?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, md: 12 }}>
              <PdfFileNameGenerator
                control={control}
                formOptions={formOptions}
                reloadAbbreviations={reloadAbbreviations}
              />
            </Grid>
          </Grid>
        </FormSection>

        <Divider sx={{ my: 4 }} />

        <FormSection title="Location Details">
          <Grid container spacing={3}>
            <Grid size={11}>
              <CustomAutocomplete
                name="sitename1"
                control={control}
                label="Site Name"
                options={["Add New", ...formOptions.sitename1]}
                error={!!errors.sitename1}
                helperText={errors.sitename1?.message}
                isLoading={isLoadingOptions}
                onChange={(_, newValue) => {
                  if (newValue === "Add New") {
                    handleAddNewDropdownValue("sitename1");
                  }
                }}
              />
            </Grid>
            <Grid
              size={1}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Tooltip title="Add additional site name">
                <IconButton
                  color="primary"
                  aria-label="add site"
                  onClick={handleAddSite}
                  disabled={additionalSites >= 2}
                  variant="outlined"
                  sx={{ border: "1px solid" }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>

            {additionalSites >= 1 && (
              <Grid size={12}>
                <CustomAutocomplete
                  name="sitename2"
                  control={control}
                  label="Site Name 2"
                  options={["Add New", ...formOptions.sitename2]}
                  error={!!errors.sitename2}
                  helperText={errors.sitename2?.message}
                  isLoading={isLoadingOptions}
                  onChange={(_, newValue) => {
                    if (newValue === "Add New") {
                      handleAddNewDropdownValue("sitename2");
                    }
                  }}
                />
              </Grid>
            )}

            {additionalSites >= 2 && (
              <Grid size={12}>
                <CustomAutocomplete
                  name="sitename3"
                  control={control}
                  label="Site Name 3"
                  options={["Add New", ...formOptions.sitename3]}
                  error={!!errors.sitename3}
                  helperText={errors.sitename3?.message}
                  isLoading={isLoadingOptions}
                  onChange={(_, newValue) => {
                    if (newValue === "Add New") {
                      handleAddNewDropdownValue("sitename3");
                    }
                  }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 3 }}>
              <Controller
                name="streetNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    slotProps={{
                      input: {
                        min: 0,
                        step: 1,
                        onWheel: (e) => e.target.blur(),
                      },
                    }}
                    label="Street Number"
                    placeholder="Enter street number"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 9 }}>
              <CustomAutocomplete
                name="addressname"
                control={control}
                label="Street"
                options={["Add New", ...formOptions.addressname]}
                error={!!errors.addressname}
                helperText={errors.addressname?.message}
                isLoading={isLoadingOptions}
                onChange={(_, newValue) => {
                  if (newValue === "Add New") {
                    handleAddNewDropdownValue("addressname");
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="berth"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Berth"
                    placeholder="Enter berth number"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="building"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Building"
                    placeholder="Enter building name/number"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="portarea"
                control={control}
                defaultValue={[]}
                render={({ field: { onChange, value, ...field } }) => {
                  // Ensure value is always an array
                  const selectedValues = Array.isArray(value) ? value : [];

                  return (
                    <FormControl fullWidth error={!!errors.portarea}>
                      <InputLabel>Port Area</InputLabel>
                      <Select
                        {...field}
                        multiple
                        value={selectedValues}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          // Handle "Add New" option
                          if (newValue.includes("Add New")) {
                            handleAddNewDropdownValue("portarea");
                            return;
                          }
                          // Update with the new array of values
                          onChange(newValue);
                        }}
                        label="Port Area"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              width: "auto",
                              minWidth: 250,
                            },
                          },
                        }}
                        IconComponent={
                          isLoadingOptions
                            ? () => <CircularProgress size={20} />
                            : undefined
                        }
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {formOptions.portarea.map((area) => (
                          <MenuItem
                            key={area}
                            value={area}
                            sx={{ whiteSpace: "normal" }}
                          >
                            {area}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.portarea && (
                        <FormHelperText>
                          {errors.portarea.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />
            </Grid>
          </Grid>
        </FormSection>

        <Divider sx={{ my: 4 }} />

        <FormSection title="Classification">
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="division"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.division}>
                    <InputLabel>Revenue Division</InputLabel>
                    <Select
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => {
                        if (e.target.value === "Add New") {
                          handleAddNewDropdownValue("division");
                          return;
                        }
                        field.onChange(e);
                      }}
                      label="Revenue Division"
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            width: "auto",
                            minWidth: 250,
                          },
                        },
                      }}
                      IconComponent={
                        isLoadingOptions
                          ? () => <CircularProgress size={20} />
                          : undefined
                      }
                    >
                      {formOptions.division.map((division) => (
                        <MenuItem
                          key={division}
                          value={division}
                          sx={{ whiteSpace: "normal" }}
                        >
                          {division}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.division && (
                      <FormHelperText>{errors.division.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.category}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      {...field}
                      onChange={(e) => {
                        if (e.target.value === "Add New") {
                          handleAddNewDropdownValue("category");
                          return;
                        }
                        field.onChange(e);
                      }}
                      label="Category"
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            width: "auto",
                            minWidth: 250,
                          },
                        },
                      }}
                      IconComponent={
                        isLoadingOptions
                          ? () => <CircularProgress size={20} />
                          : undefined
                      }
                    >
                      {formOptions.category.map((category) => (
                        <MenuItem
                          key={category}
                          value={category}
                          sx={{ whiteSpace: "normal" }}
                        >
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.category && (
                      <FormHelperText>{errors.category.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </FormSection>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={isSubmitting || isResourceLoading}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            sx={{ minWidth: 200 }}
          >
            {isSubmitting
              ? "Submitting..."
              : isResourceLoading
              ? "Loading Resources..."
              : "Submit Document"}
          </Button>
          {isResourceLoading && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 1, textAlign: "center" }}
            >
              Please wait while resources are loading...
            </Typography>
          )}
        </Box>

        <AddNewDropdownValue
          open={isAddDropdownValueDialogOpen}
          onClose={handleCloseDropdownValueDialog}
          onSubmit={handleDropdownValueSubmit}
          instance={instance}
          accounts={accounts}
          tenantId={tenantId}
          fieldName={currentDropdownField}
        />
      </form>
    );
  }
);

export default FormContent;
